// The MIT License (MIT)
//
// Copyright (c) 2016-2021 Camptocamp SA
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

/**
 * Application entry point.
 *
 * This file includes `import`'s for all the components/directives used
 * by the HTML page and the controller to provide the configuration.
 */

import angular from 'angular';
import epfl_authgeoportailBase from '../epfl_authgeoportailmodule.js';
import epfl_authgeoportailCommon from './Common.js';
import epfl_authgeoportailShortcutsComponent from '../shortcuts/component.js';
import epfl_authgeoportailFloorComponent from '../floor/component.js';
import epfl_authgeoportailSearchComponent from '../search/component.js';
import epfl_authgeoportailFiltersNumberCoordinates from '../filters/NumberCoordinates.js';
import epfl_authgeoportailPrintfacultyComponent from '../printfaculty/component.js';
import epfl_authgeoportailRoutingToolsComponent from '../routingtools/component.js';
import gmfAbstractDesktopController, {
  AbstractDesktopController,
} from 'gmf/controllers/AbstractDesktopController.js';

import './sass/pro.scss';

/**
 * @private
 */
class Controller extends AbstractDesktopController {
  /**
   * @param {angular.IScope} $scope Scope.
   * @param {angular.auto.IInjectorService} $injector Main injector.
   * @param {angular.IHttpService} $http Angular's $http service.
   * @param {angular.IWindowService} $window The Angular $window service.
   * @param {string} rolesUrl The roles URL.
   * @param {string} currentInterfaceUrl The current interface URL.
   * @param {string} authenticationBaseUrl The current interface URL.
   */
  constructor($scope, $injector, $window, $http, rolesUrl, currentInterfaceUrl, authenticationBaseUrl) {
    super($scope, $injector);

    if (document.location.href.includes('itinerar')) {
      console.log('Hack: hiding tooltip class, see GSEPF-269');
      const style = document.createElement('style');
      style.innerHTML = '.tooltip { display: none; }';
      document.head.appendChild(style);
    }

    // Injected Properties

    /**
     * @type {string}
     * @export
     */
    this.currentInterfaceUrl = currentInterfaceUrl;

    /**
     * @type {string}
     * @export
     */
    this.authenticationBaseUrl = authenticationBaseUrl;

    /**
     * @type {angular.$window}
     * @private
     */
    this.$window_ = $window;

    /**
     * @type {angular.IHttpService}
     * @private
     */
    this.$http_ = $http;

    // Inner properties

    /**
     * @type {Array.<Object>|undefined}
     * @export
     */
    this.availableRoles = undefined;

    /**
     * @type {Object|undefined}
     * @export
     */
    this.selectedRole = undefined;

    /**
     * @type {boolean}
     * @export
     */
    this.showPrintFaculty = false;

    // Allow angular-gettext-tools to collect the strings to translate
    /** @type {angular.gettext.gettextCatalog} */
    const gettextCatalog = $injector.get('gettextCatalog');
    gettextCatalog.getString('Add a theme');
    gettextCatalog.getString('Add a sub theme');
    gettextCatalog.getString('Add a layer');
    // translation for routing UI
    gettextCatalog.getString('corridor');
    gettextCatalog.getString('default');
    gettextCatalog.getString('highway');
    gettextCatalog.getString('office');
    gettextCatalog.getString('road');
    // translation for search group results
    gettextCatalog.getString('bancomat');
    gettextCatalog.getString('bibliotheques');
    gettextCatalog.getString('culture');
    gettextCatalog.getString('espaces_culturels');
    gettextCatalog.getString('facultes');
    gettextCatalog.getString('information_general');
    gettextCatalog.getString('livraison');
    gettextCatalog.getString('locaux');
    gettextCatalog.getString('parking_2_roues');
    gettextCatalog.getString('plan_bornes_camipro');
    gettextCatalog.getString('plan_chargeurs_monetiques');
    gettextCatalog.getString('plan_hotel');
    gettextCatalog.getString('plan_logements_etudiants');
    gettextCatalog.getString('plan_points_de_rassemblement');
    gettextCatalog.getString('plan_prises_voiture');
    gettextCatalog.getString('plan_transports_publics');
    gettextCatalog.getString('points_de_rassemblement');
    gettextCatalog.getString('prises_velos');
    gettextCatalog.getString('restauration');
    gettextCatalog.getString('services_epfl');
    gettextCatalog.getString('station_postale');
    gettextCatalog.getString('persons');
    gettextCatalog.getString('pro');

    /**
     * @type {epfl_authgeoportail.Common}
     * @export
     */
    this.common = new epfl_authgeoportailCommon(this, $scope, $injector);

    // Get available roles
    $http.get(rolesUrl).then((result) => {
      this.availableRoles = result['data']['roles'].sort((a, b) => a.name.localeCompare(b.name));
      const unWatch = $scope.$watch(
        () => this.gmfUser.roles,
        (roles) => {
          if (roles && roles.length > 0) {
            const roleName = roles[0].name;
            if (roleName === 'Geoportail_Facultés_AppGrpU') {
              this.showPrintFaculty = true;
            }
            if (roleName) {
              this.selectedRole = this.availableRoles.find((role) => role.id === roleName);
              unWatch();
            }
          }
        },
      );
    });

    /**
     * @type {Object} selected editable layer
     * @export
     */
    this.selectedEditableTreeCtrl = null;

    // watch the editable layers list for change and change dimension for itineraries
    $scope.$watch(
      () => this.selectedEditableTreeCtrl,
      (newValue, oldValue, scope) => {
        if (newValue) {
          const layer_name = newValue.node.layers;
          const ref_name = 'itineraries_pending_';
          if (layer_name.indexOf(ref_name) > -1) {
            // will display only selected routing layer with 100% opacity
            this.dimensions['routingfloor'] = parseInt(layer_name.substr(ref_name.length).replace('_', '-'));
          }
        } else {
          // will display all routing layers with 100% opacity
          this.dimensions['routingfloor'] = '99';
        }
      },
    );
  }

  /**
   * Reload the page with the corresponding role
   * * @export
   */
  changeRole() {
    if (!this.selectedRole) {
      return;
    }
    this.$http_
      .get(`${this.authenticationBaseUrl}loginuser?map_param_role=${this.selectedRole.id}`)
      .then((result) => {
        this.$window_.location.href = `${this.currentInterfaceUrl}?map_param_role=${this.selectedRole.id}`;
      });
  }
}
Controller.$inject = [
  '$scope',
  '$injector',
  '$window',
  '$http',
  'rolesUrl',
  'currentInterfaceUrl',
  'authenticationBaseUrl',
];

/**
 * @hidden
 */
const gmfModule = angular.module('Apppro', [
  epfl_authgeoportailBase.name,
  epfl_authgeoportailCommon.module.name,
  gmfAbstractDesktopController.name,
  epfl_authgeoportailShortcutsComponent.name,
  epfl_authgeoportailFloorComponent.name,
  epfl_authgeoportailSearchComponent.name,
  epfl_authgeoportailFiltersNumberCoordinates.name,
  epfl_authgeoportailPrintfacultyComponent.name,
  epfl_authgeoportailRoutingToolsComponent.name,
]);

gmfModule.value('gmfContextualdatacontentTemplateUrl', 'epfl/contextualdata');

gmfModule.run([
  '$templateCache',
  /**
   * @param {angular.ITemplateCacheService} $templateCache
   */
  ($templateCache) => {
    $templateCache.put('epfl/contextualdata', require('../contextualdata.html'));
    $templateCache.put('gmf/drawing/featureStyleComponent', require('../drawing/featureStyleComponent.html'));
    $templateCache.put('gmf/query/windowComponent', require('../displayquerywindow/displayquerywindow.html'));
  },
]);

gmfModule.controller('ProController', Controller);

export default gmfModule;
